import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CPENZIcon } from "@/components/CPENZIcon";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import api from "@/lib/api";

const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);

export default function AuthDialog({ open, onOpenChange, onSuccess }) {
  const { t } = useLang();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success(t("toast.saved"));
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const google = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    let mode = "emergent";
    try { const { data } = await api.get("/auth/google/config"); mode = data.mode; } catch {}
    if (mode === "direct") {
      const redirectUri = window.location.origin + "/auth/google";
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start?redirect_uri=${encodeURIComponent(redirectUri)}`;
      return;
    }
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] rounded-2xl p-0 overflow-hidden" data-testid="auth-dialog">
        <DialogTitle className="sr-only">{t("auth.welcome")}</DialogTitle>
        <DialogDescription className="sr-only">{t("auth.subtitle")}</DialogDescription>
        <div className="px-7 pt-8 pb-6">
          <CPENZIcon size={44} glow />
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-[#0b0f1a]">{t("auth.welcome")}</h2>
          <p className="text-sm text-slate-500 mt-1">{t("auth.subtitle")}</p>

          <Button
            type="button" onClick={google} data-testid="auth-google-btn"
            variant="outline"
            className="w-full mt-6 rounded-xl h-11 font-medium border-slate-200"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4 mr-2" />
            {t("auth.google")}
          </Button>
          {isSafari && (
            <p className="mt-2 text-[11px] leading-snug text-slate-400" data-testid="auth-safari-hint">{t("auth.safari_hint")}</p>
          )}

          <div className="flex items-center gap-3 my-5">
            <span className="h-px flex-1 bg-slate-100" />
            <span className="text-xs text-slate-400">{t("auth.or")}</span>
            <span className="h-px flex-1 bg-slate-100" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <Label className="text-xs text-slate-500">{t("auth.name")}</Label>
                <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" />
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">{t("auth.email")}</Label>
              <Input data-testid="auth-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">{t("auth.password")}</Label>
              <Input data-testid="auth-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl mt-1" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full rounded-xl h-11 bg-[#0b0f1a] text-white">
              {loading ? "…" : mode === "login" ? t("auth.login_cta") : t("auth.register_cta")}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-4">
            {mode === "login" ? t("auth.no") : t("auth.have")}{" "}
            <button
              data-testid="auth-toggle"
              className="font-semibold text-[#7c3aed]"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
            >
              {mode === "login" ? t("auth.register") : t("auth.login")}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
